import { Header } from "semantic-ui-react";
import { useHistory, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

import {
  InputCheckBox,
  InputText,
  InputToggle,
} from "../../../components/Inputs/Inputs";
import BackButton from "../../../components/BackButton/BackButton";
import { api } from "../../../api/api";
import { useToast } from "../../../components/Toast";
import {
  delivery_boy_feilds_url,
  country_url,
  delivery_boy_sections_url,
} from "../../../api/urls";
import unknownError from "../../../utils/unknownError";
import Loading from "../../../components/Loading/Loading";
import { moveUpToError } from "../../../utils/moveUpToError";
import { modules } from "../../../api/codes";
import { APIDropdown } from "../../../components/APIDropdown/APIDropdown";
import { generateUID } from "../../../utils/generateUID";
import { useFetch } from "../../../utils/useFetch";
import { ApprovalText } from "../../../utils/approveLevel";

const initialState = {
  code: "",
  description: "",
  printable_name: "",
  disable: false,
  is_active: false,
  doc_name: "",
  doc_kyc: false,
  doc_certificate: false,
  filerequire: false,
  expiryrequire: false,
  doc_num_require: false,
  country: "",
  section: "",
};

const module = modules.DeliveryBoyFeilds;

const title = "Delivery Boy Fields";
const backUrl = "/masters/delivery_boy_fields";
const apiUrl = delivery_boy_feilds_url;

const NewDeliveryBoyFields = ({ edit, view }) => {
  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const history = useHistory();
  const params = useParams();
  const toast = useToast();
  const [isGetting, setIsGetting] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [aprvlLoader, setAprvlLoader] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    if (edit || view || params.id) {
      const getData = async () => {
        try {
          const res = await api.get(apiUrl + params.id + "/", {
            headers: {
              module: module,
            },
          });
          setData(res.data);
          setIsGetting(false);
        } catch (err) {
          unknownError(err);
          setIsGetting(false);
        }
      };

      getData();
    } else {
      setIsGetting(false);
    }
  }, [params.id, edit, view, refresh]);

  const set = (key, value) => {
    setData((d) => {
      const newData = { ...d };
      newData[key] = value;
      return newData;
    });
  };

  const sectionsData = useFetch(delivery_boy_sections_url);
  const sections = sectionsData?.data?.map((d) => d.section);

  if (isGetting) return <Loading />;

  return (
    <form
      id="form-wrapper"
      onSubmit={(e) => {
        e.preventDefault();
        setIsSaving(true);
        const newData = { ...data };
        newData.code = generateUID();
        newData.printable_name = generateUID();
        try {
          if (edit && params.id) {
            api
              .patch(apiUrl + data.id + "/", newData, {
                headers: {
                  "Content-Type": "application/json",
                  module: module,
                },
              })
              .then((res) => {
                toast.open("Edited data with id: " + res.data.id, "success");
                history.push(backUrl);
                setIsSaving(false);
              })
              .catch((err) => {
                if (err.response.data) {
                  setErrors(err.response.data);
                  moveUpToError();
                } else {
                  unknownError(err);
                }
                setIsSaving(false);
              });
          } else {
            api
              .post(apiUrl, newData, {
                headers: {
                  "Content-Type": "application/json",
                  module: module,
                },
              })
              .then((res) => {
                toast.open("Added data with id: " + res.data.id, "success");
                history.push(backUrl);
                setIsSaving(false);
              })
              .catch((err) => {
                if (err.response.data) {
                  setErrors(err.response.data);
                  moveUpToError();
                } else {
                  unknownError(err);
                }
                setIsSaving(false);
              });
          }
        } catch (err) {
          unknownError(err);
          setIsSaving(false);
        }
      }}
    >
      <div id="form">
        <BackButton href={backUrl} />
        <Header>{title}</Header>
        <InputText
          label="Document name"
          placeholder="Document name"
          disabled={view}
          value={data.doc_name}
          error={errors["doc_name"]}
          pattern="[A-Za-z]+"
          patternError={"Only alphabets are allowed"}
          required
          onChange={(v) => set("doc_name", v)}
        />
        <InputCheckBox
          label="Document number required"
          disabled={view}
          value={data.doc_num_require}
          error={errors["doc_num_require"]}
          onChange={(v) => set("doc_num_require", v)}
        />
        <InputCheckBox
          label="Document expiry required"
          disabled={view}
          value={data.expiryrequire}
          error={errors["expiryrequire"]}
          onChange={(v) => set("expiryrequire", v)}
        />
        <InputCheckBox
          label="Document file required"
          disabled={view}
          value={data.filerequire}
          error={errors["filerequire"]}
          onChange={(v) => set("filerequire", v)}
        />
        <APIDropdown
          label="Section"
          placeholder="Section"
          required
          apiData={{ data: sections, loading: sectionsData.isLoading }}
          disabled={view}
          value={data.section}
          onChange={(v) => set("section", v)}
          error={errors["section"]}
        />
        <APIDropdown
          label="Country"
          placeholder="Country"
          required
          disabled={view}
          value={data.country}
          url={country_url}
          onChange={(v) => set("country", v)}
          error={errors["country"]}
        />
        <InputToggle
          label="Active"
          disabled={view}
          value={data.is_active}
          error={errors["is_active"]}
          onChange={(v) => set("is_active", v)}
        />

        <ApprovalText
          id={params.id}
          level={data?.approval}
          setRefresh={setRefresh}
          setAprvlLoader={setAprvlLoader}
          toast={toast}
          view={view}
          aprvlLoader={aprvlLoader}
          module={module}
          apiUrl={apiUrl}
        />
        {!view && (
          <>
            <div style={{ padding: "10px" }} />
            <div className="actions">
              <button
                className="btn-red"
                type="button"
                onClick={() => {
                  history.push(backUrl);
                }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => {
                  moveUpToError();
                }}
                disabled={isSaving}
              >
                Save
              </button>
            </div>
          </>
        )}
        <div style={{ padding: "10px" }} />
      </div>
    </form>
  );
};

export default NewDeliveryBoyFields;
